import { useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { AtSign, Calendar, FileText, Moon, Settings, Sun, Wand2 } from "lucide-react";

import { useStore } from "./context/StoreContext";
import { setNavigate } from "./lib/navigation";
import Sidebar from "./components/Sidebar";
import GenerateModal from "./components/GenerateModal";
import { Logo, LogoMark } from "./components/branding";
import { ErrorBanner, GeneratingOverlay, Spinner, Toast } from "./components/ui";

export const NAV = [
  { to: "/calendar", label: "Calendar", icon: Calendar },
  { to: "/documents", label: "Knowledge", icon: FileText },
  { to: "/accounts", label: "Accounts", icon: AtSign },
  { to: "/prompt", label: "Prompt", icon: Wand2 },
  { to: "/settings", label: "Settings", icon: Settings },
];

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

export default function App({ children }) {
  const navigate = useNavigate();
  const {
    dark,
    setDark,
    loading,
    bootError,
    refreshAll,
    openGenerate,
    connectedPlatforms,
    documents,
    gen,
    genError,
    setGenError,
    toast,
  } = useStore();

  // Let the store (and other non-component code) navigate through the router.
  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);

  useEffect(() => {
    document.title = "Remifo";
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-muted">
        <Logo size={34} />
        <Spinner size={22} />
      </div>
    );
  }

  if (bootError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-surface border border-border rounded-2xl p-6 text-center">
          <div className="flex justify-center mb-4">
            <Logo size={34} />
          </div>
          <h1 className="text-[18px] font-semibold text-foreground">Cannot reach the backend</h1>
          <p className="text-[13.5px] text-muted mt-2">
            Remifo could not connect to the API at <span className="text-foreground">{API_URL}</span>. Start the backend, then retry.
          </p>
          <pre className="text-[12px] text-muted bg-popover border border-border rounded-xl p-3 mt-4 text-left overflow-auto">
{`cd backend
uvicorn app.main:app --reload --port 8000`}
          </pre>
          <button
            onClick={refreshAll}
            className="mt-4 bg-accent text-accent-foreground rounded-xl px-4 py-2.5 text-sm font-semibold transition hover:opacity-90 active:scale-[0.97]"
          >
            Retry connection
          </button>
        </div>
      </div>
    );
  }

  const canGenerate = connectedPlatforms.length > 0 && documents.length > 0;

  return (
    <div className="min-h-screen">
      <div className="flex min-h-screen">
        <Sidebar nav={NAV} />

        <main className="flex-1 min-w-0 flex flex-col">
          <header className="md:hidden sticky top-0 z-30 glass border-b border-border flex items-center justify-between px-4 py-3 safe-top">
            <Logo size={26} />
            <div className="flex items-center gap-1">
              <button
                onClick={() => setDark(!dark)}
                className="w-9 h-9 rounded-xl flex items-center justify-center text-muted hover:bg-foreground/5 transition active:scale-[0.97]"
                title="Theme"
              >
                {dark ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <button
                onClick={openGenerate}
                disabled={!canGenerate}
                className="bg-accent text-accent-foreground rounded-xl px-3 py-2 text-[13px] font-semibold flex items-center gap-1.5 transition active:scale-[0.97]"
              >
                Generate
              </button>
            </div>
          </header>

          <div className="px-4 sm:px-7 lg:px-9 py-6 sm:py-8 pb-28 md:pb-10 flex-1 max-w-[1280px] w-full mx-auto">
            {children}
          </div>
        </main>
      </div>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 glass border-t border-border safe-bottom">
        <div className="flex items-stretch">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `flex-1 flex flex-col items-center gap-1 py-2.5 transition active:scale-[0.97] ${
                    isActive ? "text-foreground" : "text-faint"
                  }`
                }
              >
                <Icon size={20} />
                <span className="text-[10.5px] font-medium">{n.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      <Toast toast={toast} />
      <GenerateModal />
      <GeneratingOverlay gen={gen} logoSlot={<LogoMark size={26} className="animate-pulse" />} />
      <ErrorBanner message={genError} onDismiss={() => setGenError(null)} />
    </div>
  );
}